import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function CaliforniaToCarolinaRoutePage() {
  // Dynamically manage SEO browser titles & meta descriptions
 useEffect(() => {
  document.title = "Tesla Shipping Routes USA | State-to-State EV Transport";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Explore popular Tesla shipping routes across the USA, including California to Texas, California to Florida, Hawaii transport options, and nationwide EV shipping services.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'Ship Tesla California to Texas, Ship Tesla from California to Florida, Cross-country Tesla transport USA, Tesla shipping routes USA, Interstate EV transport services, Tesla car shipping routes USA, Tesla transport California to Texas, Tesla transport California to Florida, Coast-to-coast Tesla shipping, Tesla shipping to Hawaii costs, Long-distance EV transport, Door-to-door Tesla shipping USA, State-to-state Tesla transport, Geo-targeted vehicle transport service, Electric vehicle shipping nationwide'
  );
}, []);

  const faqs = [
    {
      question: "How long does it take to ship a Tesla from California to the Carolinas?",
      answer: "Transit times depend on your exact pickup and delivery cities, as well as seasonal conditions. We provide estimated delivery windows at the time of booking, and our team handles coordination end to end to keep the transit smooth."
    },
    {
      question: "What is the recommended transport method for this route?",
      answer: "We offer both open and enclosed carrier options. Open transport is the standard industry selection for general relocations, while enclosed transport is highly recommended for owners looking for maximum protection from road debris and weather across long distances."
    }
  ];

  // Combined Graph Schema (Service Route + Dynamic FAQPage)
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Tesla Shipping California-to-Carolina",
        "areaServed": [
          { "@type": "State", "name": "California" },
          { "@type": "State", "name": "North Carolina" },
          { "@type": "State", "name": "South Carolina" }
        ],
        "serviceType": "Vehicle Transport"
      },
      {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does it take to ship a Tesla from California to the Carolinas?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Transit times typically range from 5 to 9 days depending on pickup location, carrier availability, and final delivery destination in North or South Carolina."
      }
    },
    {
      "@type": "Question",
      "name": "Is enclosed transport recommended for Tesla vehicles?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Enclosed transport is recommended for maximum protection against weather and road debris, especially for premium Tesla models, although open carriers remain a safe and cost-effective option."
      }
    },
    {
      "@type": "Question",
      "name": "How is a Tesla secured during vehicle transport?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tesla vehicles are secured using EV-safe soft-strap tie-down systems, low-angle loading techniques, and proper battery charge management to ensure safe and compliant transit."
      }
    },
    {
      "@type": "Question",
      "name": "Can I track my Tesla during shipping?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, real-time tracking and automated status updates are provided throughout the entire shipping process to ensure full visibility."
      }
    },
    {
      "@type": "Question",
      "name": "Is my Tesla insured during transport?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All carriers are fully licensed and insured, with coverage in place to protect your Tesla throughout the shipping journey."
      }
    }
  ]
      },
      {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Tesla Vehicle Shipping from California to the Carolinas",
  "description": "Specialized Tesla vehicle transport service from California to North and South Carolina using EV-certified carriers. Offers both open and enclosed auto transport with real-time tracking, secure handling, and full insurance coverage.",
  "provider": {
    "@type": "Organization",
    "name": "ShipTesla",
    "url": "https://shiptesla.com"
  },
  "areaServed": [
    {
      "@type": "State",
      "name": "California"
    },
    {
      "@type": "State",
      "name": "North Carolina"
    },
    {
      "@type": "State",
      "name": "South Carolina"
    }
  ],
  "serviceType": "Electric Vehicle Transport",
  "availableChannel": {
    "@type": "ServiceChannel",
    "serviceLocation": {
      "@type": "Place",
      "name": "United States"
    },
    "availableLanguage": [
      "English"
    ]
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Tesla Shipping Options",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Open Carrier Tesla Transport"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Enclosed Carrier Tesla Transport"
        }
      }
    ]
  },
  "areaServed": {
    "@type": "AdministrativeArea",
    "name": "United States"
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "Tesla Owners, EV Buyers, Relocating Individuals"
  }
}


    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Injecting Structured SEO Data into the DOM */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Cross-Country Routes</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground" data-testid="route-title">
              Ship a Tesla from California to the Carolinas
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg border-l-2 border-accent pl-4 text-left italic my-6">
              Transporting your Tesla from California to North or South Carolina is a streamlined, precision-managed process powered by an EV-specialized logistics network. Whether you opt for cost-efficient open carriers or fully enclosed transport for maximum protection, we match your vehicle with rigorously vetted, EV-trained carriers—ensuring secure, door-to-door delivery with zero compromise on handling standards.
            </p>
            <div className="mt-6">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Main Content Body */}
          <div className="space-y-8 text-muted-foreground md:text-lg leading-relaxed mb-12">
            <p>
              Designed for today’s mobility needs—relocations, multi-state moves, or direct-from-dealership deliveries—our operations leverage optimized interstate transport corridors to deliver consistent, predictable transit performance. Every shipment adheres to electric vehicle–specific protocols, including soft-strap securement, low-clearance loading techniques, and battery charge management best practices throughout the journey.
            </p>
            <p>
              Our logistics coordination layer integrates California dispatch hubs with key metropolitan destinations across North and South Carolina, minimizing transit friction and operational delays. Real-time tracking, automated status notifications, and proactive communication workflows ensure complete visibility from pickup through final delivery—aligned with modern AI-driven customer experience expectations.
            </p>
          </div>

          {/* Route-Specific FAQs */}
          <div className="mt-12 border-t border-border pt-12">
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-5 text-foreground">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA Block */}
          <div className="mt-16 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Guides Links Layout */}
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="font-outfit text-lg font-semibold text-foreground mb-4">Related guides</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ship-a-tesla" className="text-accent hover:underline">How to Ship a Tesla</Link></li>
              <li><Link to="/tesla-shipping-cost" className="text-accent hover:underline">Tesla Shipping Cost Guide</Link></li>
            </ul>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}


